import React, { useState, useRef, useEffect, useMemo } from "react";
import { Card, Input, Label } from "reactstrap";
import { Plus, PlusCircle, Search, SearchIcon } from "lucide-react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import CommonTable from "../CommonTable";
import AddDepartment from "./AddDepartment";
import { useLoader } from "../../_helper/LoaderContext";
import { addGlobalDepartment, deleteDepartment, getDepartmentList, getGlobalDepartmentList, statusDepartment } from "./service/department-service";
import { toast } from "react-toastify";
import Select, { components } from 'react-select';

// Custom Checkbox Option
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};
// Select All Option
const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px',
        height: '44px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '44px',
        padding: '0 8px',
        overflowX: 'auto',        // This enables horizontal scroll
        overflowY: 'hidden',
        flexWrap: 'nowrap',       // Important: prevents wrapping to new line
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',   // Firefox
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,  // Prevents shrinking
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};


export default function DepartmentHome() {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false)
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [data, setData] = useState({})
    const [department, setDepartment] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();
    const [imported, setImported] = useState("add")
    // Global units from master list
    const [unitMaster, setUnitMaster] = useState([]);

    // Selected global units (for filtering or submission)
    const [selectedGlobalUnits, setSelectedGlobalUnits] = useState([]);


    // Transform unitMaster into react-select format
    const unitOptions = useMemo(() => {
        const options = unitMaster.map(u => ({
            label: u.name,
            value: u._id,
            description: u.description || ""
        }));
        return unitMaster.length > 0 ? [allOption, ...options] : [];
    }, [unitMaster]);

    // Handle Select Change with "Select All" logic
    const handleGlobalUnitChange = (selected) => {
        // "selected" comes from react-select (may include "Select All")
        if (!selected) {
            setSelectedGlobalUnits([]);
            return;
        }

        const hasSelectAll = selected.some(opt => opt.value === "*");

        if (hasSelectAll) {
            // Toggle Select All
            if (selectedGlobalUnits.length === unitMaster.length) {
                setSelectedGlobalUnits([]); // Deselect all
            } else {
                // Select all real units
                setSelectedGlobalUnits(unitMaster.map(u => ({
                    label: u.name,
                    value: u._id
                })));
            }
        } else {
            // Normal multi-select behavior
            // Filter out the "Select All" option from value
            const realSelections = selected.filter(opt => opt.value !== "*");
            setSelectedGlobalUnits(realSelections);
        }
    };

    useEffect(() => {
        getList()
    }, [currentPage, entriesPerPage, search])

    const getList = async () => {
        setLoading(true)
        try {
            const form_data = new FormData()
            form_data.append("search", search);
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);
            const res = await getDepartmentList(form_data)
            if (res.data.success == true) {
                setDepartment(res.data.data.data)
                setTotalEntries(res.data.data.totalCount || 0);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const totalPages = Math.ceil(totalEntries / entriesPerPage);


    const columns = [
        { header: "Department Name", accessor: "name" },
    ];

    const handleToggleStatus = (data) => {
        Swal.fire({
            title: "Are you sure?",
            text: data.isActive
                ? "You want to inactivate this department?"
                : "You want to activate this department?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `${data.isActive == true ? "Inactive" : "Active"}`
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true)
                try {
                    const res = await statusDepartment(data._id)
                    if (res.data.success == true) {
                        toast.success(res.data.message)
                        getList()
                    }
                } catch (error) {
                    console.log(error)
                } finally {
                    setLoading(false)
                }
            }
        });
    };

    const actions = [
        { label: "Edit", onClick: (row) => handleEdit(row) },
        { label: "Delete", onClick: (row) => detele(row) },
    ]

    const handleEdit = (row) => {
        // console.log(row)
        setData(row)
        setEdit(true)
    }

    const editClose = () => {
        setEdit(false)
    }

    const detele = (data) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this department?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `Yes, delete it!`
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true)
                try {
                    const res = await deleteDepartment(data._id)
                    if (res.data.success == true) {
                        toast.success(res.data.message)
                        getList()
                    }
                } catch (error) {
                    console.log(error)
                } finally {
                    setLoading(false)
                }
            }
        });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            getList();
        }, 500); // waits 0.5s after typing stops

        return () => clearTimeout(delayDebounce);
    }, [search, currentPage, entriesPerPage]);

    useEffect(() => {
        getGlobalDepartment()
    }, [])

    // Fetch global units
    const getGlobalDepartment = async () => {
        setLoading(true);
        try {
            const res = await getGlobalDepartmentList();
            if (res.data.success) {
                setUnitMaster(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching global units:", error);
        } finally {
            setLoading(false);
        }
    };

    // Submit selected global units
    const handleSubmitSelected = async () => {
        const selectedIds = selectedGlobalUnits
            .filter(item => item.value !== "*")
            .map(item => item.value);

        if (selectedIds.length === 0) {
            toast.info("No units selected");
            return;
        }
        const payload = {
            masterDepartmentIds: selectedIds
        }
        setLoading(true)
        try {
            const res = await addGlobalDepartment(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                handleClose()
                setSelectedGlobalUnits([])
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);
    };

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="row">
                        <div className="col-md-4">
                            <div className="input-group mb-3 ">
                                <span className="input-group-text tw-bg-white" id="basic-addon1"><SearchIcon /></span>
                                <input type="text" className="form-control icon_form" placeholder="Search By Name..." aria-label="Username" aria-describedby="basic-addon1" style={{ borderLeft: 'none' }} value={search}
                                    onChange={(e) => setSearch(e.target.value)} />
                            </div>

                        </div>

                        {/* Global Unit Multi-Select */}
                        <div className="col-md-5">
                            {/* <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                options={unitOptions}
                                value={selectedGlobalUnits}
                                onChange={handleGlobalUnitChange}
                                placeholder="Add by global departments..."
                                components={{ Option }}
                                styles={customStyles}
                                classNamePrefix="react-select"
                                isClearable={true}
                            /> */}
                        </div>

                        <div className="col-md-3 text-end">
                            {/* <button
                                className="btn btn-success me-2"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                ({selectedGlobalUnits.length})
                            </button> */}
                            <button className="btn btn-primary" onClick={handleShow}>
                                {t("Add")}
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <CommonTable
                                columns={columns}
                                data={department}
                                onToggleStatus={handleToggleStatus}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}   // pass value
                                setEntriesPerPage={setEntriesPerPage}  // pass setter
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Department</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pb-0">
                    <div className="d-flex tw-space-x-4 mb-3">
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-add"
                                type="radio"
                                name="customerType"
                                value="add"
                                checked={imported === "add"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-add" className="mb-0 pe-2">
                                Manual
                            </Label>

                        </div>
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-gloabal"
                                type="radio"
                                name="customerType"
                                value="gloabal"
                                checked={imported === "gloabal"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-gloabal" className="mb-0 pe-2">
                                Import From Database
                            </Label>
                        </div>
                            {selectedGlobalUnits.length > 0 && (
                            <Button
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                Import ({selectedGlobalUnits.length})
                            </Button>
                        )}
                    </div>
                </Modal.Body>
                {imported !== "add" ? (
                    <>
                        <Modal.Body>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                options={unitOptions}
                                value={selectedGlobalUnits}
                                onChange={handleGlobalUnitChange}
                                placeholder="Add departments from global list..."
                                components={{ Option }}
                                styles={customStyles}
                                classNamePrefix="react-select"
                                isClearable={true}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            {/* <Button
                                className="btn me-2"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                Import ({selectedGlobalUnits.length})
                            </Button> */}
                            {/* <Button variant="secondary" onClick={handleClose}>
                                {t("Close")}
                            </Button> */}
                        </Modal.Footer>
                    </>
                ) : (
                    <AddDepartment handleClose={handleClose} getList={getList} />
                )}
            </Modal>

            <Modal show={edit} onHide={editClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Department</Modal.Title>
                </Modal.Header>
                <AddDepartment handleClose={editClose} getList={getList} data={data} />
            </Modal>
        </>
    )
}
